/**
 * 시드 PRNG (mulberry32). 결정적이고 빠르며 32비트 시드 하나로 상태가 정해진다.
 * Math.random 은 절대 쓰지 않는다 — 재현 불가능한 트라이얼 리스트는 데이터가 아니다.
 */
export interface Rng {
  next(): number;                      // [0, 1)
  int(maxExclusive: number): number;   // [0, max)
  between(min: number, max: number): number;
  shuffle<T>(xs: readonly T[]): T[];
  pick<T>(xs: readonly T[]): T;
}

export function createRng(seed: number): Rng {
  let a = seed >>> 0;
  const next = (): number => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const int = (max: number): number => Math.floor(next() * max);
  return {
    next,
    int,
    between: (min, max) => min + next() * (max - min),
    shuffle: <T>(xs: readonly T[]): T[] => {
      const out = xs.slice();
      for (let i = out.length - 1; i > 0; i -= 1) {
        const j = int(i + 1);
        const tmp = out[i] as T;
        out[i] = out[j] as T;
        out[j] = tmp;
      }
      return out;
    },
    pick: <T>(xs: readonly T[]): T => {
      if (xs.length === 0) throw new Error('pick(): empty');
      return xs[int(xs.length)] as T;
    },
  };
}

/** 문자열(참가자 코드 + 세션 번호 등)에서 32비트 시드를 만든다. FNV-1a. */
export function seedFromString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
